import { RADAR_REFRESH_MS } from "@divespots/contracts";
import type {
  Favourites,
  MeFeatures,
  ObjectModelListQuery,
  ObjectModelUpdate,
  Terrain,
} from "@divespots/contracts";
import { useCallback } from "react";
import {
  QueryClient,
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useSession } from "../admin/session.ts";
import { api } from "./client.ts";
import type { ModelUploadParams } from "./client.ts";
import { isApiRequestError } from "./errors.ts";
import { notifyFavouriteError } from "./favourite-notice.ts";

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        // Without a connection the service worker may still answer (saved spots, cached lists), so
        // queries must run instead of waiting for "online". A failed request is reported at once.
        networkMode: "always",
        // Only transient failures are retried; 400/401/403/404 show their error UI at once.
        retry: (count, error) =>
          (typeof navigator === "undefined" || navigator.onLine) &&
          isApiRequestError(error) &&
          (error.code === "network" ||
            error.code === "rate_limited" ||
            error.code === "internal") &&
          count < 2,
      },
    },
  });
}

export const keys = {
  terrain: (lat: number, lon: number, radius: number) => ["terrain", lat, lon, radius] as const,
  lakes: ["lakes"] as const,
  spots: ["spots"] as const,
  spot: (slug: string) => ["spot", slug] as const,
  meFeatures: ["me", "features"] as const,
  /** Private to the account; the session change clears it with every other cache (`qc.clear()`). */
  favourites: ["me", "favourites"] as const,
  /** Decision-mail opt-out; cleared with every other cache on a session change. */
  meNotifications: ["me", "notifications"] as const,
  diveLakes: ["dive", "lakes"] as const,
  objectModels: (q: ObjectModelListQuery) => ["object-models", q] as const,
  /** Content-addressed (sha256): never stale, and kept outside the list root so list refreshes skip it. */
  objectModelFile: (sha: string | undefined) => ["object-model-file", sha] as const,
  lakeOverview: (slug: string) => ["lake-overview", slug] as const,
  wind: ["wind"] as const,
  precip: ["precip"] as const,
  radar: ["radar"] as const,
};

export const useLakes = () =>
  useQuery({
    queryKey: keys.lakes,
    queryFn: ({ signal }) => api.listLakes(signal).then((r) => r.lakes),
  });

export const useSpots = () =>
  useQuery({
    queryKey: keys.spots,
    queryFn: ({ signal }) => api.listSpots({ status: "published" }, signal).then((r) => r.spots),
  });

export const useSpot = (slug: string | undefined) =>
  useQuery({
    queryKey: keys.spot(slug ?? ""),
    queryFn: ({ signal }) => api.getSpot(slug as string, signal),
    enabled: Boolean(slug),
  });

/** Lakebed tile around a point. Never auto-retried: 404/429 have their own UI with a manual retry. */
export const useTerrain = (lat: number, lon: number, radius: number, enabled = true) =>
  useQuery({
    queryKey: keys.terrain(lat, lon, radius),
    queryFn: ({ signal }) => api.getTerrain({ lat, lon, radius }, signal),
    enabled,
    retry: false,
    staleTime: 10 * 60_000,
    // switching the radius keeps the current tile on screen until the next one has arrived
    placeholderData: keepPreviousData,
  });

/**
 * The wind forecast grid, only while the overlay is on and the device is online. Fresh for 10 min,
 * refetched every 30 min while on. Failures (503 until the API has a field) are not retried here:
 * the time bar shows "temporarily unavailable" and the next refetch tries again.
 */
export const useWindField = (enabled: boolean) =>
  useQuery({
    queryKey: keys.wind,
    queryFn: ({ signal }) => api.windField(signal),
    enabled,
    retry: false,
    staleTime: 10 * 60_000,
    refetchInterval: enabled ? 30 * 60_000 : false,
  });

/** The precipitation grid: same settings as the wind field, enabled only while its overlay is on. */
export const usePrecipField = (enabled: boolean) =>
  useQuery({
    queryKey: keys.precip,
    queryFn: ({ signal }) => api.precipField(signal),
    enabled,
    retry: false,
    staleTime: 10 * 60_000,
    refetchInterval: enabled ? 30 * 60_000 : false,
  });

/** Seconds to wait before asking the radar index again after a 503, when the answer says so. */
export const radarRetryDelayMs = (error: unknown, fallbackMs: number): number => {
  const s = isApiRequestError(error) ? error.retryAfterSec : null;
  return s !== null && s > 0 ? s * 1000 : fallbackMs;
};

/**
 * The radar frame index, only while "Niederschlag" is on and the device is online. Refetched every
 * RADAR_REFRESH_MS while the tab is visible; after a 503 (no fresh frame) it asks again after the
 * `Retry-After` the answer carried. Never auto-retried by the query itself.
 */
export const useRadarIndex = (enabled: boolean, visible: boolean) =>
  useQuery({
    queryKey: keys.radar,
    queryFn: ({ signal }) => api.radarIndex(signal),
    enabled,
    retry: false,
    staleTime: RADAR_REFRESH_MS,
    refetchInterval:
      enabled && visible ? (q) => radarRetryDelayMs(q.state.error, RADAR_REFRESH_MS) : false,
  });

const NO_FEATURES: MeFeatures = { dive: false };

export interface MeFeaturesState extends MeFeatures {
  isPending: boolean;
  /** The request failed for a reason other than 401/403 (server, network, offline). */
  isError: boolean;
  /** The server says there is no session (401), whatever the cached `/api/me` thinks. */
  signedOut: boolean;
  refetch: () => void;
}

/**
 * What the signed-in caller may use (`/api/me/features`). Short staleTime: a grant or revoke
 * applies on the next visit. A 403 (banned) means no features; a 401 sets `signedOut`; any other
 * failure is `isError`, never "no access".
 */
export const useMeFeatures = (enabled = true): MeFeaturesState => {
  const q = useQuery({
    queryKey: keys.meFeatures,
    queryFn: async ({ signal }): Promise<MeFeatures & { signedOut: boolean }> => {
      try {
        return { ...(await api.meFeatures(signal)), signedOut: false };
      } catch (e) {
        if (isApiRequestError(e) && e.code === "unauthorized")
          return { ...NO_FEATURES, signedOut: true };
        if (isApiRequestError(e) && e.code === "forbidden")
          return { ...NO_FEATURES, signedOut: false };
        throw e;
      }
    },
    enabled,
    retry: false,
    staleTime: 30_000,
  });
  return {
    dive: enabled ? (q.data?.dive ?? false) : false,
    signedOut: enabled ? (q.data?.signedOut ?? false) : false,
    isPending: enabled && q.isPending,
    isError: enabled && q.isError,
    refetch: () => void q.refetch(),
  };
};

/** Lakes with a built dive tile set; only ask when the caller has dive access. */
export const useDiveLakes = (enabled: boolean) =>
  useQuery({
    queryKey: keys.diveLakes,
    queryFn: ({ signal }) => api.listDiveLakes(signal).then((r) => r.lakes),
    enabled,
    retry: false,
    staleTime: 60_000,
  });

/**
 * The lake's overview terrain for the mini-map. A lake without one (404) resolves to null, so the
 * map is simply absent; any other failure is an error and the map is absent too. Never retried.
 */
export const useLakeOverview = (slug: string | null | undefined) =>
  useQuery({
    queryKey: keys.lakeOverview(slug ?? ""),
    queryFn: async ({ signal }): Promise<Terrain | null> => {
      try {
        return await api.lakeOverview(slug as string, signal);
      } catch (e) {
        if (isApiRequestError(e) && e.code === "not_found") return null;
        throw e;
      }
    },
    enabled: Boolean(slug),
    retry: false,
    staleTime: 30 * 60_000,
  });

/** Models of the objects the caller may see in a spot or a lake (exactly one of `spotId` / `lake`). */
export const useObjectModels = (query: ObjectModelListQuery | null) =>
  useQuery({
    queryKey: keys.objectModels(query ?? { spotId: "" }),
    queryFn: ({ signal }) =>
      api.listObjectModels(query as ObjectModelListQuery, signal).then((r) => r.models),
    enabled: query !== null,
    retry: false,
  });

/** Admin writes for one object's model; every success refreshes the model lists. */
export function useObjectModelMutations(objectId: string) {
  const qc = useQueryClient();
  const done = () => qc.invalidateQueries({ queryKey: ["object-models"] });
  return {
    upload: useMutation({
      mutationFn: ({ file, ...p }: { file: ArrayBuffer } & ModelUploadParams) =>
        api.putObjectModel(objectId, file, p),
      onSuccess: (model, { file }) => {
        qc.setQueryData(keys.objectModelFile(model.sha256), file);
        return done();
      },
    }),
    update: useMutation({
      mutationFn: (body: ObjectModelUpdate) => api.updateObjectModel(objectId, body),
      onSuccess: done,
    }),
    remove: useMutation({
      mutationFn: () => api.deleteObjectModel(objectId),
      onSuccess: done,
    }),
  };
}

const NO_FAVOURITES: ReadonlySet<string> = new Set();
/** Module-level so the query memoises the set (a new function would rebuild it on every render). */
const favouriteIdSet = (f: Favourites): ReadonlySet<string> => new Set(f.spotIds);

export interface FavouritesState {
  /** The favourite spot ids (empty until the query has succeeded, and while signed out). */
  ids: ReadonlySet<string>;
  /** Signed in and the list has arrived: the only state in which the filter and the pin badges may act. */
  ready: boolean;
  /** Signed in and the list has not arrived yet. */
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
}

/** The caller's favourite spot ids (`/api/me/favourites`), asked for only when signed in. */
export function useFavourites(): FavouritesState {
  const { status } = useSession();
  const enabled = status === "user";
  const q = useQuery({
    queryKey: keys.favourites,
    queryFn: ({ signal }) => api.listFavourites(signal),
    enabled,
    staleTime: 30_000,
    select: favouriteIdSet,
  });
  return {
    ids: enabled && q.data ? q.data : NO_FAVOURITES,
    ready: enabled && q.isSuccess,
    isPending: enabled && q.isPending,
    isError: enabled && q.isError,
    refetch: () => void q.refetch(),
  };
}

/** Writes in flight per client: toggles of one spot run one after the other, then the list is refetched. */
interface FavouriteQueue {
  tails: Map<string, Promise<void>>;
  /** The newest toggle's number per spot: only it may roll back, an older failure is superseded. */
  latest: Map<string, number>;
  pending: number;
}
const favouriteQueues = new WeakMap<QueryClient, FavouriteQueue>();

function setFavouriteInCache(qc: QueryClient, spotId: string, on: boolean) {
  qc.setQueryData<Favourites>(keys.favourites, (old) => {
    const ids = (old?.spotIds ?? []).filter((id) => id !== spotId);
    return { spotIds: on ? [spotId, ...ids] : ids };
  });
}

/**
 * Stars or un-stars a spot. Optimistic: the list changes at once; a failed write rolls that spot
 * back and shows a notice. Toggles of the same spot are serialised (the next waits for the previous
 * to settle) and, once the last write has settled, the list is refetched so client and server
 * cannot disagree. Resolves to whether the write was accepted.
 */
export function useToggleFavourite() {
  const qc = useQueryClient();
  return useCallback(
    (spotId: string, on: boolean): Promise<boolean> => {
      let queue = favouriteQueues.get(qc);
      if (!queue)
        favouriteQueues.set(qc, (queue = { tails: new Map(), latest: new Map(), pending: 0 }));
      const q = queue;
      void qc.cancelQueries({ queryKey: keys.favourites });
      setFavouriteInCache(qc, spotId, on);
      q.pending += 1;
      const seq = (q.latest.get(spotId) ?? 0) + 1;
      q.latest.set(spotId, seq);
      const run = async (): Promise<boolean> => {
        try {
          await (on ? api.addFavourite(spotId) : api.removeFavourite(spotId));
          return true;
        } catch (e) {
          // a later toggle of this spot is queued: its optimistic state stands and its write decides
          if (q.latest.get(spotId) !== seq) return false;
          setFavouriteInCache(qc, spotId, !on);
          notifyFavouriteError(isApiRequestError(e) && e.code === "conflict" ? "full" : "failed");
          return false;
        }
      };
      const result = (q.tails.get(spotId) ?? Promise.resolve()).then(run);
      const tail = result.then(() => {
        q.pending -= 1;
        if (q.tails.get(spotId) === tail) {
          q.tails.delete(spotId);
          q.latest.delete(spotId);
        }
        if (q.pending === 0)
          void qc.invalidateQueries({ queryKey: keys.favourites, refetchType: "all" });
      });
      q.tails.set(spotId, tail);
      return result;
    },
    [qc],
  );
}
